import React, { useState } from 'react';
import {
    RECEIPT_ACCEPT_ATTRIBUTE,
    validateReceiptFile,
} from '../../../utils/vacationReceiptFiles';
import {
    FORM_FILE_REQUIRED,
    FORM_LABELS,
    NO_REQUEST_OPTION_LABEL,
} from '../vacationReceipts.definitions';
import type { ReceiptUploadFormProps } from '../vacationReceipts.types';
import type { ReceiptRequestOption } from '../../../utils/vacationReceiptLinks';

const INPUT_CLASSES =
    'mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-50';

export const ReceiptUploadForm: React.FC<ReceiptUploadFormProps> = ({
    isUploading,
    uploadProgress,
    requestOptions = [],
    onSubmit,
    onCancel,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [optionId, setOptionId] = useState('');
    const [referenceStartDate, setReferenceStartDate] = useState('');
    const [referenceEndDate, setReferenceEndDate] = useState('');
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const findOption = (id: string): ReceiptRequestOption | undefined => {
        for (const group of requestOptions) {
            const found = group.options.find((opt) => opt.id === id);
            if (found) return found;
        }
        return undefined;
    };

    const linkedRequest = findOption(optionId) ?? null;

    const handleRequestChange = (nextId: string) => {
        setOptionId(nextId);

        const option = findOption(nextId);
        if (option) {
            setReferenceStartDate(option.startDate);
            setReferenceEndDate(option.endDate);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files?.[0] ?? null;
        setFile(selected);
        setError(selected ? validateReceiptFile(selected) : null);
    };

    const handleSubmit = () => {
        if (!file) {
            setError(FORM_FILE_REQUIRED);
            return;
        }

        const fileError = validateReceiptFile(file);
        if (fileError) {
            setError(fileError);
            return;
        }

        if (!referenceStartDate) {
            setError('A data de início é obrigatória');
            return;
        }

        if (!referenceEndDate) {
            setError('A data de término é obrigatória');
            return;
        }

        if (referenceEndDate < referenceStartDate) {
            setError('A data de término não pode ser antes da data de início');
            return;
        }

        const option = findOption(optionId);

        if (option?.hasReceipt) {
            setError('Este período já tem um recibo anexado. Para corrigir, exclua o anterior e anexe o novo.');
            return;
        }

        onSubmit({
            file,
            requestId: option?.requestId || null,
            segmentOrder: option?.segmentOrder ?? null,
            referenceStartDate,
            referenceEndDate,
            note: note.trim(),
        });
    };
      
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== 'Enter') return;

        const target = event.target as HTMLElement;
        if (target.tagName === 'TEXTAREA') return;

        event.preventDefault();
        handleSubmit();
    };

    return (
        <div
            onKeyDown={handleKeyDown}
            className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4"
        >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                    <label htmlFor="receipt-file" className="block text-sm font-medium text-gray-700">
                        {FORM_LABELS.file} <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="receipt-file"
                        type="file"
                        accept={RECEIPT_ACCEPT_ATTRIBUTE}
                        onChange={handleFileChange}
                        disabled={isUploading}
                        className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-60"
                    />
                </div>

                {requestOptions.length > 0 && (
                    <div className="sm:col-span-2">
                        <label
                            htmlFor="receipt-request"
                            className="block text-sm font-medium text-gray-700"
                        >
                            {FORM_LABELS.request}
                        </label>
                        <select
                            id="receipt-request"
                            value={optionId}
                            onChange={(event) => handleRequestChange(event.target.value)}
                            disabled={isUploading}
                            className={INPUT_CLASSES}
                        >
                            <option value="">{NO_REQUEST_OPTION_LABEL}</option>
                            {requestOptions.map((group) => (
                                group.label ? (
                                    <optgroup key={group.label} label={group.label}>
                                        {group.options.map((option) => (
                                            <option key={option.id} value={option.id} disabled={option.hasReceipt}>
                                                {option.label}
                                                {option.hasReceipt ? ' · recibo anexado' : ''}
                                            </option>
                                        ))}
                                    </optgroup>
                                ) : (
                                    group.options.map((option) => (
                                        <option key={option.id} value={option.id} disabled={option.hasReceipt}>
                                            {option.label}
                                            {option.hasReceipt ? ' · recibo anexado' : ''}
                                        </option>
                                    ))
                                )
                            ))}
                        </select>
                    </div>
                )}

                <div>
                    <label
                        htmlFor="receipt-reference-start"
                        className="block text-sm font-medium text-gray-700"
                    >
                        {FORM_LABELS.referenceStart}
                    </label>
                    <input
                        id="receipt-reference-start"
                        type="date"
                        value={referenceStartDate}
                        onChange={(event) => setReferenceStartDate(event.target.value)}
                        disabled={isUploading}
                        readOnly={Boolean(linkedRequest)}
                        className={INPUT_CLASSES}
                    />
                </div>

                <div>
                    <label
                        htmlFor="receipt-reference-end"
                        className="block text-sm font-medium text-gray-700"
                    >
                        {FORM_LABELS.referenceEnd}
                    </label>
                    <input
                        id="receipt-reference-end"
                        type="date"
                        value={referenceEndDate}
                        min={referenceStartDate || undefined}
                        onChange={(event) => setReferenceEndDate(event.target.value)}
                        disabled={isUploading}
                        readOnly={Boolean(linkedRequest)}
                        className={INPUT_CLASSES}
                    />
                </div>

                {linkedRequest && (
                    <p className="text-xs text-gray-500 sm:col-span-2">
                        {FORM_LABELS.linkedHint}
                    </p>
                )}

                <div className="sm:col-span-2">
                    <label htmlFor="receipt-note" className="block text-sm font-medium text-gray-700">
                        {FORM_LABELS.note}
                    </label>
                    <input
                        id="receipt-note"
                        type="text"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        placeholder={FORM_LABELS.notePlaceholder}
                        disabled={isUploading}
                        className={INPUT_CLASSES}
                    />
                </div>
            </div>

            {error && (
                <p role="alert" className="mt-3 text-sm text-red-600">
                    {error}
                </p>
            )}

            {isUploading && (
                <div className="mt-4">
                    <div
                        className="h-2 w-full rounded-full bg-gray-200"
                        role="progressbar"
                        aria-valuenow={Math.round(uploadProgress)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        <div
                            className="h-2 rounded-full bg-indigo-600 transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                    <p className="mt-1 text-center text-sm text-gray-500">
                        {Math.round(uploadProgress)}%
                    </p>
                </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isUploading}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                    {FORM_LABELS.cancel}
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isUploading}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                    {isUploading ? FORM_LABELS.submitting : FORM_LABELS.submit}
                </button>
            </div>
        </div>
    );
};

export default ReceiptUploadForm;
